import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';


import { PlotlyViaWindowModule } from 'angular-plotly.js';
import { uniq } from 'lodash';
import { Layout, PlotData } from 'plotly.js';
import { BehaviorSubject, firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

import { ShakerControlFeatureSelectionComponent } from 'src/app/components/configuration-input-feature-module/shaker-control/shaker-control-feature-selection/shaker-control-feature-selection.component';
import { SafeResizeHandlerDirective } from 'src/app/directives/plotly/reset-zoom-buttons/safe-resize-handler.directive';
import { IonicImports, MaterialImportsModule } from 'src/app/material-imports.module';
import { ShakerControlSequence } from 'src/app/model/shaker-control/shaker-control-sequence';
import { ShakerFeatureDescriptor } from 'src/app/model/shaker-control/shaker-feature-descriptor';
import { LogService } from 'src/app/services/loggers/logger.service';
import { ShakerProfileFeatureService } from 'src/app/services/shaker-profile/shaker-profile-feature.service';
import { UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { loadPlotly } from 'src/app/utility/plotly';
import { TIME_DESCRIPTOR } from 'src/app/utility/shaker-control/shaker-control-sequence-time-descriptor';
import { convertShakerSequenceToPlotData, getShakerSequencePlotLayoutAndFeatureToAxisMapping, getShakerSequencePlotLayoutBase } from 'src/app/utility/shaker-control/shaker-control-sequence-to-graph';
import { createNameTakenValidator } from 'src/app/utility/validators/name-taken-validator';
import { DefaultConfig } from 'src/assets/default.config';
import { SubSink } from 'subsink';
import { SplitButtonComponent } from '../../../buttons/split-button/split-button.component';
import { DialogControl } from '../../dialog/dialog-control';
import { IIsDialog } from '../../dialog/dialog.service';
import { ShakerProfileValueInputComponent } from './shaker-profile-value-input/shaker-profile-value-input.component';


/**
 * The data passed to the shaker-profile dialog.
 */
export type ShakerProfileDialogInputData = {

  /**
   * The name of the profile.
   *
   * If not provided, the profile is considered to be a new profile.
   */
  readonly profileName?: string;


  /**
   * The initial sequence of the profile.
   */
  readonly initialValues: ShakerControlSequence;


  /**
   * The control sequence of the profile which is not initial values.
   */
  readonly sequence: ShakerControlSequence;


  /**
   * Whether users cannot modify the sequences.
   */
  readonly readonly: boolean;
};


/**
 * The result returned from the shaker-profile dialog after closing it.
 *
 * This is either a valid result or `undefined` if the dialog was closed by
 * "canceling" it.
 */
export type ShakerProfileDialogResult =
  undefined
  | {

    /**
     * Profile ID
     *
     * - ID of the profile that was just created.
     * - Special value indicating an a profile to be applied but not to be
     *   saved to the database for re-use at other controls.
     *   {@link UserDataService.customTemplateIndicator}
     */
    readonly profileId: string;


    /**
     * Initial values
     */
    readonly initialValues: ShakerControlSequence;


    /**
     * The control sequence of the profile which is not initial values.
     */
    readonly sequence: ShakerControlSequence;


    /**
     * Features used in the profile.
     *
     * ID/Key of the feature (e.g. 'RPM' or 'TIME').
     */
    readonly features: string[];
  };


/**
 * A single entry in the sequence-table of {@link ShakerProfileDialogComponent}.
 */
type TableEntry = {
  type?: 'CARBON_DIOXIDE' | 'RPM' | 'HUMIDITY' | 'TEMPERATURE' | 'TIME';
  typeDisplayName?: string;
  unit?: string;

  value?: any;
  min?: number;
  max?: number;
  step?: number;

  error?: {

    /**
     * The id of the error.
     * Can be used to match other TableEntry which relate to the same error instance.
     */
    id: string;

    /**
     * The reason for the error. Displayed to the user.
     */
    reason: string;
  };
};


@Component({
  selector: 'app-shaker-profile-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialImportsModule,
    IonicImports,
    PlotlyViaWindowModule,
    SplitButtonComponent,
    ShakerProfileValueInputComponent,
    ShakerControlFeatureSelectionComponent,
    SafeResizeHandlerDirective,
  ],
  templateUrl: './shaker-profile-dialog.component.html',
  styleUrls: ['./shaker-profile-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShakerProfileDialogComponent implements OnInit, OnDestroy,
 IIsDialog<ShakerProfileDialogInputData, ShakerProfileDialogResult> {
  public readonly __compileTimeTypeInferenceHelper: [ShakerProfileDialogInputData, ShakerProfileDialogResult][] = [];

  @ViewChild('buttonRow', { read: TemplateRef<unknown> })
  private set buttonRowTemplate(template: TemplateRef<unknown>) {
    this.dialogControl.setActionButtonsTemplate(template);
  }


  @ViewChild(
    'profileNameOverlay',
    {
      static: true,
      read: ElementRef<HTMLElement>
    }
  )
  private _profileNameOverlay!: ElementRef<HTMLElement>;


  /**
   * @private
   */
  @ViewChild('plotWrapper', { static: true, read: ElementRef })
  private _plotWrapper?: ElementRef;


  /**
   * The columns to display in the sequence-table.
   *
   * @private
   */
  protected readonly columns = ['pre', 'type', 'value', 'actions'];


  /**
   * Whether dragging is enabled for the sequence-table.
   *
   * Used as a workaround, because the drag handle is not respected in the rows.
   * Will be enabled once the user starts pressing on the drag-column of a row
   * and disabled again once the user drops the row.
   * Otherwise the whole row acts as its drag handle.
   *
   * @private
   */
  protected draggingEnabled = false;


  /**
   * The selectable features for the value-inputs.
   *
   * IMPORTANT: if the TIME feature exists, it needs to be the last one in the list.
   *
   * @private
   */
  protected selectedFeatures: ShakerFeatureDescriptor[] = [];


  /**
   * The initial values the sequence starts with/is initialized with.
   *
   * Provided to the dialog by the caller as part of {@link ShakerProfileDialogInputData}.
   */
  protected initialValues: TableEntry[] = [];


  /**
   * Subject containing the data to be shown in the sequence-table.
   */
  protected readonly data$ = new BehaviorSubject<TableEntry[]>([]);


  /**
   * The data-source for the sequence-table.
   *
   * @private
   */
  protected readonly dataSource$ = this.data$.asObservable();


  /**
   * Whether the sequence contains errors.
   *
   * E.g. when between two TIME entries two entries of the same type appear.
   *
   * @private
   */
  protected sequenceContainsErrors = false;


  /**
   * Data rendered by plotly
   *
   * @private
   */
  protected plotData: Partial<PlotData>[] = [];


  /**
   * Layout for the plot
   *
   * @private
   */
  protected plotLayout: Partial<Layout> = getShakerSequencePlotLayoutBase();


  /**
   * Form control to acquire the name for new profiles
   */
  protected readonly newProfileNameControl = new FormControl<string>(
    '',
    [
      Validators.required,
      Validators.minLength(3)
    ]
  );


  /**
   * @private
   */
  protected profileNameErrorMessage$ = this.newProfileNameControl.statusChanges
    .pipe(
      startWith(this.newProfileNameControl.status),
      filter(status => status === 'INVALID' || status === 'VALID'),
      map(status => {
        if (status === 'VALID') {
          return '';
        }

        const errors = this.newProfileNameControl.errors;
        switch (true) {
          case errors === null:
            return '';
          case errors!.required:
            return 'Profile name is required.';
          case errors!.minlength: {
            const required = errors!.minLength.requiredLength;
            const actual   = errors!.minLength.actualLength;
            return `Profile name must be at least ${required} characters long (got ${actual}).`;
          }
          case errors!.nameTaken:
            return errors!.nameTaken;
          default:
            return 'Invalid profile name.';
        }
      })
    );


  private availableFeatureList$ = this.shakerFeatureService.getFeatures();


  /**
   * Features that should be controlled by the shaker profile.
   *
   * Picked by users directly in the UI or pre-picked from an existing profile
   * (passed to this dialog).
   *
   * @private
   */
  public get _pickedFeatures(): ShakerFeatureDescriptor[] {
    return this.selectedFeatures;
  }


  /**
   * @private
   */
  public set _pickedFeatures(features: ShakerFeatureDescriptor[]) {
    this.selectedFeatures = deepCopySync(features);

    if (features.length) {
      this.selectedFeatures.push(TIME_DESCRIPTOR);
    }

    // Remove de-selected entries from data
    const data = this.data$.getValue();
    const newData = data.filter(entry => {
      const feature = this.selectedFeatures.find(f => f.id === entry.type);
      return feature !== undefined;
    });

    this.data$.next(newData);

    // Handle initial data, run checks and recreate the plot
    const v = this.convertTableToControlSequence(this.initialValues, true);
    void this.prepareInitialValues(v)
      .then(() => {
        this.runChecks();
        this.recreatePlot();
        this.changeDetectorRef.markForCheck();
      });

  }


  /**
   * Tracks rxjs-subscriptions
   */
  protected readonly subsink = new SubSink();


  /**
   * Used to trigger the recreation of the plot.
   */
  protected readonly recreatePlotSubject$ = new Subject<void>();


  /**
   * Whether the dialog is in readonly mode, preventing modifications to sequences.
   */
  public get readonly(): boolean {
    return this.input.readonly;
  }


  private readonly input: ShakerProfileDialogInputData;


  public readonly title$: Observable<string>;


  /**
   * To indicate progress
   */
  protected isSubmitting = signal(false);

  constructor(
    private dialogControl: DialogControl<ShakerProfileDialogInputData, ShakerProfileDialogResult>,
    private changeDetectorRef: ChangeDetectorRef,
    private log: LogService,
    private shakerFeatureService: ShakerProfileFeatureService,
    private uiNotificationService: UINotificationService,
    private userDataService: UserDataService
  ) {
    this.input = this.dialogControl.data;
    if (this.input.readonly) {
      this.title$ = of('View shaker profile');
    } else {
      this.title$ = of('Edit shaker profile');
    }

    void loadPlotly();
    // Hook up the trigger for the plot recreation to the logic that actually
    // creates the plot-data.
    this.subsink.sink = this.recreatePlotSubject$
      .pipe(
        debounceTime(DefaultConfig.general.userInputDefaultDebounce_Milliseconds),
        switchMap(() => this.recreatePlotImplementation()),
        // Queue change detection to update the graph
        tap(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe();
  }


  /**
   * @private
   */
  public async ngOnInit() {
    this.addProfileNameControlAsyncValidators();

    await this.preselectFeaturesFromInput();

    await this.prepareInitialValues(this.input.initialValues);
    this.data$.next(await this.convertControlSequenceToTableSequence(this.input.sequence));

    this.runChecks();
    this.recreatePlot();
  }


  public ngOnDestroy() {
    this.subsink.unsubscribe();
  }


  private async preselectFeaturesFromInput() {
    const availableFeatures = await this.availableFeatureList$;
    const features = uniq([...this.input.initialValues, ...this.input.sequence]
      .map(entry => entry.type));

    this._pickedFeatures = availableFeatures.filter(
      a => features.includes(a.id)
    );
  }


  private addProfileNameControlAsyncValidators() {
    const templates$ = this.userDataService.getAllTemplates().then(list => list.filter(
      template => template.template.payload.inputElementType === 'shaker-control'
    ).map(template => ({ _id: template._id, name: template.template.name })));

    const asyncValidators = [
      createNameTakenValidator(
        from(templates$),
        () => '',
        'Profile name {} is already taken.'
      ),
    ];

    this.newProfileNameControl.addAsyncValidators(asyncValidators);
  }


  private async prepareInitialValues(existingValues: ShakerControlSequence) {

    const controlSequence: ShakerControlSequence = [];

    // For each type, add an entry to the control sequence, but keep the value
    // from inputValues if it exists.
    //
    // We assume, that the time feature is the last entry in the list of selectable
    // features.

    for (const type of this.selectedFeatures) {
      const existingEntry = existingValues.find(entry => entry.type === type.id);

      controlSequence.push({
        type: type.id,
        value: existingEntry?.value
      });
    }

    this.initialValues = await this.convertControlSequenceToTableSequence(controlSequence);
  }


  /**
   * Add a new part to the profile sequence.
   *
   * @private
   */
  protected _addSequencePart(): void {
    const data = deepCopySync(this.data$.getValue());
    data.push({});

    // Update the table data
    this.data$.next(data);

    // Check if the last row is visible
    this.scrollLastRowIntoView();
    this.runChecks();
  }


  /**
   * Add a new part to the profile sequence.
   *
   * @private
   */
  protected _addSelectedFeatureSequenceBlock(): void {
    const data = deepCopySync(this.data$.getValue());

    // For each selected feature add a new row to the table
    for (const feature of this.selectedFeatures) {
      if (feature.id === 'TIME') {
        continue;
      }

      data.push({
        max: feature.max,
        min: feature.min,
        step: feature.step,
        type: feature.id as TableEntry['type'],
        typeDisplayName: feature.name,
      });
    }

    // Append one time entry
    data.push({
      min: TIME_DESCRIPTOR.min,
      max: TIME_DESCRIPTOR.max,
      type: TIME_DESCRIPTOR.id as TableEntry['type'],
      typeDisplayName: TIME_DESCRIPTOR.name,
      step: TIME_DESCRIPTOR.step
    });

    // Update the table data
    this.data$.next(data);

    // Check if the last row is visible
    this.scrollLastRowIntoView();
    this.runChecks();
  }


  /**
   * Remove the part at the specified index from the profile sequence.
   *
   * @param idx Index of the row
   */
  protected _removeSequencePart(idx: number): void {
    const data = deepCopySync(this.data$.getValue());
    data.splice(idx, 1);
    this.data$.next(data);

    this.runChecks();
    this.recreatePlot();
  }


  /**
   * Create a copy of the provided entry at the end of the sequence.
   *
   * @param entry The table-entry to copy
   */
  protected _copySequencePart(entry: TableEntry) {
    const data = deepCopySync(this.data$.getValue());
    data.push(entry);
    this.data$.next(data);

    this.runChecks();
    this.scrollLastRowIntoView();
    this.recreatePlot();
  }


  /**
   * Called when the type of a  value in the table is changed by the user.
   *
   * Changes the type of the corresponding entry.
   *
   * @private
   * @param idx Index of the row
   * @param type The new type. Corresponds to the `id` of a feature-descriptor.
   *            {@link ShakerFeatureDescriptor}
   */
  protected async _valueTypeChanged(idx: number, type: TableEntry['type']) {
    const data = deepCopySync(this.data$.getValue());

    const types = this.selectedFeatures;
    const feature = types.find(feature => feature.id === type);

    const tableEntry = data[idx];
    tableEntry.type = type;
    tableEntry.typeDisplayName = feature?.name;
    tableEntry.min = feature?.min;
    tableEntry.max = feature?.max;
    tableEntry.step = feature?.step;

    this.data$.next(data);
    this.runChecks();
    this.recreatePlot();

    this.changeDetectorRef.markForCheck();
  }


  /**
   * Called when the value of a part in the table is changed by the user.
   *
   * @private
   * @param idx
   * @param $event
   */
  protected _updateSequencePartValue(idx: number, value: any, sequence: 'initial' | 'mutable') {
    const data = sequence === 'mutable' ? this.data$.getValue() : this.initialValues;
    data[idx].value = value;

    this.runChecks();
    if (!this.sequenceContainsErrors) {
      this.recreatePlot();
    }
  }


  protected _onSequencePartDrop($event: CdkDragDrop<any>) {
    this.draggingEnabled = false;

    const data = deepCopySync(this.data$.getValue());
    moveItemInArray(data, $event.previousIndex, $event.currentIndex);

    this.data$.next(data);
    this.runChecks();

    if (!this.sequenceContainsErrors) {
      this.recreatePlot();
    }
  }


  /**
   * Returns the full sequence consisting of both, the initial and the
   * mutable sequence parts.
   */
  private getFullTableSequence(): TableEntry[] {
    const data = this.data$.getValue();

    const merged: TableEntry[] = [];

    this.initialValues.forEach(e => merged.push(e));
    data.forEach(e => merged.push(e));

    return merged;
  }


  /**
   * Check if the sequence contains errors.
   *
   * E.g. when between two TIME entries two entries of the same type appear.
   *
   * The result will be stored in {@link _sequenceContainsErrors}.
   */
  private runChecks(): void {
    const sequence = this.getFullTableSequence();

    this.sequenceContainsErrors = false;

    // Ensure that not two entries of the same type appear between two
    // TIME entries. If so, set the error property.
    let lastTypes: TableEntry['type'][] = [];
    for (let i = 0; i < sequence.length; ++i) {
      const entry = sequence[i];

      if (entry.type === undefined) {
        this.sequenceContainsErrors = true;
        continue;
      }

      if (entry.value === undefined || entry.value === null || entry.value === '') {
        this.sequenceContainsErrors = true;
      }

      entry.error = undefined;

      if (entry.type === 'TIME') {
        lastTypes = [];
        continue;
      }

      if (lastTypes.includes(entry.type)) {
        entry.error = {
          id: 'duplicate-type',
          reason: 'Duplicate type'
        };

        this.sequenceContainsErrors = true;

        // Find the previous entries of the same type and set their error properties
        // as well.
        for (let j = i - 1; j >= 0; --j) {
          if (sequence[j].type === entry.type) {
            sequence[j].error = {
              id: 'duplicate-type',
              reason: 'Duplicate type'
            };
          } else if (sequence[j].type === 'TIME') {
            break;
          }
        }
      } else {
        lastTypes.push(entry.type);
      }
    }
  }


  /**
   * Convert a shaker control sequence into a sequence which can be displayed
   * by the sequence-table.
   */
  private async convertControlSequenceToTableSequence(
    source: ShakerControlSequence
  ): Promise<TableEntry[]> {
    const types = this.selectedFeatures;

    return source.map<TableEntry>(entry => {
      const feature = types.find(feature => feature.id === entry.type);

      return {
        typeDisplayName: feature?.name,
        type: entry.type as TableEntry['type'],
        value: entry.value,
        max: feature?.max,
        min: feature?.min,
        step: feature?.step,
        unit: feature?.unit
      };
    });
  }


  /**
   * Recreates the plot-data.
   *
   * Note: triggers change detection
   *
   * Actual implementation is in {@link recreatePlotImplementation}.
   */
  private recreatePlot() {
    this.recreatePlotSubject$.next();
  }


  /**
   * The actual implementation which re-creates the plot-data.
   */
  private async recreatePlotImplementation() {
    const featureDescriptors = await this.availableFeatureList$;

    const fullSequence = this.convertTableToControlSequence(
      this.getFullTableSequence(),
      true
    );

    const viewSize =
      this._plotWrapper?.nativeElement.clientWidth
        ? { width: this._plotWrapper.nativeElement.clientWidth }
        : undefined;

    const { layout, mapping } = getShakerSequencePlotLayoutAndFeatureToAxisMapping(
      {
        sequence: fullSequence,
        featureDescriptors
      },
      viewSize
    );

    this.plotLayout = layout;

    this.plotData = convertShakerSequenceToPlotData(
      {
        featureDescriptors,
        sequence: fullSequence
      },
      mapping
    );
  }


  /**
   * Scrolls the last table row into view.
   */
  private scrollLastRowIntoView() {
    const tableElement = document.getElementById('sequence-table');
    if (tableElement) {
      const isLastRowVisible = tableElement.scrollHeight - tableElement.scrollTop === tableElement.clientHeight;
      if (!isLastRowVisible) {
        // Wait for the table to re-render
        setTimeout(() => {
          // Scroll the bottom of the table into view
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 0);
      }
    }
  }


  /**
   * @private
   *
   * Will emit, if the user accepted the new name they entered or if they canceled
   * the request for a new name
   */
  protected profileNameOverlayResult$ = new Subject<'accepted' | 'canceled'>();


  /**
   * Returns the profile and optionally saves it for later use.
   *
   * @private
   * @param mode If 'create', the profile is saved for later use.
   */
  protected async _submit(mode: 'apply' | 'create' = 'apply') {
    const initialValues = this.convertTableToControlSequence(this.initialValues);
    const sequence      = this.convertTableToControlSequence(this.data$.getValue());
    const features      = this.selectedFeatures.map(f => f.id);

    let id: string = UserDataService.customTemplateIndicator;
    if (mode === 'create') {
      // Show the profile name overlay

      this._profileNameOverlay.nativeElement.classList.toggle('hidden');

      try {
        this.isSubmitting.set(true);
        const nameConfirmation = (await firstValueFrom(this.profileNameOverlayResult$))!;
        if (nameConfirmation === 'canceled') {
          return;
        }

        if (this.newProfileNameControl.invalid || this.newProfileNameControl.value === null) {
          throw new Error('Invalid shaker profile name');
        }

        const name = this.newProfileNameControl.value;

        const result = await this.shakerFeatureService.createProfileForCurrentUser(
          name,
          {
            initialValues,
            sequence,
            features
          }
        );

        id = result._id!;
      } catch (error) {
        this.log.error('Failed to create shaker profile', error);

        this.uiNotificationService.displayErrorAlert(
          {
            title: 'Failed to create shaker profile',
            message: 'An error occurred while trying to create the shaker profile. Please try again.'
          },
          error
        );

        // Do not close the dialog
        return;
      } finally {
        this._profileNameOverlay.nativeElement.classList.toggle('hidden');
        this.isSubmitting.set(false);
      }
    }

    await this.dialogControl.requestCloseDialog({
      initialValues,
      sequence,
      profileId: id,
      features
    });
  }


  private convertTableToControlSequence(
    tableSequence: TableEntry[],
    noThrow = false
  ): ShakerControlSequence {
    if (
      tableSequence.some(entry => entry.type === undefined || entry.value === undefined)
      && !noThrow
    ) {
      throw new Error('Invalid table sequence');
    }

    return tableSequence.map(entry => ({
      type: entry.type!,
      value: entry.value
    }));
  }


  protected closeDialog() {
    void this.dialogControl.requestCloseDialog(undefined);
  }
}

